// Wizard steps: Predplatna a ceny + Pravni udaje.
//
// Subscriptions mirror the reference: up to 6 tiers, Apple price tier chart,
// billing period, optional free trial, localized name (30) + description (45).
// A HALF-FILLED tier lives only in local state; the save sends complete tiers
// (id + tier + period) - the server would reject the rest (reference rule).
import { useState } from "react";
import type { AppLegalPayload, AppSubscriptionTier, AppSubscriptionsPayload } from "../../../shared/app-wizard";
import {
  APP_BILLING_PERIODS,
  APP_FREE_TRIAL_DAYS,
  APP_MAX_SUBSCRIPTION_TIERS,
  APP_TEXT_LIMITS,
  APPLE_PRICE_TIERS,
  appWizardLocales
} from "../../../shared/app-wizard";
import {
  CharCount,
  FieldBlock,
  LocaleTextRows,
  SaveBadge,
  useStepSaver,
  type StepProps
} from "./wizard-shared";

type DraftTier = AppSubscriptionTier;

const completeTier = (tier: DraftTier): boolean => tier.appleTierId.length > 0;

export function StepSubscriptions(props: StepProps) {
  const [tiers, setTiers] = useState<DraftTier[]>(props.data.wizard.steps.subscriptions?.subscriptions ?? []);
  const saver = useStepSaver("subscriptions", props.save, props.readOnly);
  const locales = appWizardLocales(props.data.wizard);
  const currency = props.data.wizard.steps.basics?.defaultCurrency || "USD";

  const persist = (next: DraftTier[]) => {
    setTiers(next);
    const payload: AppSubscriptionsPayload = { subscriptions: next.filter(completeTier) };
    saver.queue(payload as unknown as Record<string, unknown>);
  };

  const patchTier = (id: string, patch: Partial<DraftTier>) => {
    persist(tiers.map((tier) => (tier.id === id ? { ...tier, ...patch } : tier)));
  };

  const addTier = () => {
    if (tiers.length >= APP_MAX_SUBSCRIPTION_TIERS) return;
    persist([...tiers, {
      id: crypto.randomUUID().slice(0, 8),
      appleTierId: "",
      billingPeriod: "monthly",
      displayName: {},
      description: {}
    }]);
  };

  return (
    <div className="appwiz-step">
      <header className="appwiz-step-heading">
        <h2>{props.t("appWizard.subscriptions.title")}</h2>
        <p>{props.t("appWizard.subscriptions.copy")}</p>
        <SaveBadge state={saver.state} t={props.t} />
      </header>

      {tiers.length === 0 && <p className="appwiz-inline-rule">{props.t("appWizard.subscriptions.empty")}</p>}

      {tiers.map((tier, index) => (
        <article className="appwiz-tier" key={tier.id}>
          <header className="appwiz-tier-head">
            <h3>{props.t("appWizard.subscriptions.tierLabel")} {index + 1}</h3>
            {!props.readOnly && (
              <button className="text-button" type="button" onClick={() => persist(tiers.filter((item) => item.id !== tier.id))}>
                {props.t("appWizard.subscriptions.removeTier")}
              </button>
            )}
          </header>
          {!completeTier(tier) && <p className="appwiz-tier-draft-note">{props.t("appWizard.subscriptions.draftNote")}</p>}

          <div className="appwiz-tier-grid">
            <label className="appwiz-tier-field">
              <span>{props.t("appWizard.subscriptions.priceLabel")}</span>
              <select
                value={tier.appleTierId}
                disabled={props.readOnly}
                onChange={(event) => patchTier(tier.id, { appleTierId: event.target.value })}
              >
                <option value="">{props.t("appWizard.subscriptions.pricePlaceholder")}</option>
                {APPLE_PRICE_TIERS.map((price) => (
                  <option key={price.id} value={price.id}>{`$${price.usd.toFixed(2)}`}</option>
                ))}
              </select>
              <small>{props.t("appWizard.subscriptions.priceHint").replace("{currency}", currency)}</small>
            </label>

            <label className="appwiz-tier-field">
              <span>{props.t("appWizard.subscriptions.periodLabel")}</span>
              <select
                value={tier.billingPeriod}
                disabled={props.readOnly}
                onChange={(event) => patchTier(tier.id, { billingPeriod: event.target.value as DraftTier["billingPeriod"] })}
              >
                {APP_BILLING_PERIODS.map((period) => (
                  <option key={period} value={period}>{props.t(`appWizard.periods.${period}`)}</option>
                ))}
              </select>
            </label>

            <label className="appwiz-tier-field">
              <span>{props.t("appWizard.subscriptions.trialLabel")}</span>
              <select
                value={tier.freeTrialDays !== undefined ? String(tier.freeTrialDays) : ""}
                disabled={props.readOnly}
                onChange={(event) => patchTier(tier.id, {
                  freeTrialDays: event.target.value === "" ? undefined : Number(event.target.value)
                })}
              >
                <option value="">{props.t("appWizard.subscriptions.trialNone")}</option>
                {APP_FREE_TRIAL_DAYS.map((days) => (
                  <option key={days} value={days}>{days} {props.t("appWizard.subscriptions.trialDays")}</option>
                ))}
              </select>
            </label>
          </div>

          <FieldBlock label={props.t("appWizard.subscriptions.nameLabel")} hint={props.t("appWizard.subscriptions.nameHint")}>
            <LocaleTextRows
              locales={locales} value={tier.displayName} max={APP_TEXT_LIMITS.subscriptionDisplayName}
              csrfToken={props.csrfToken} readOnly={props.readOnly}
              ariaLabel={props.t("appWizard.subscriptions.nameLabel")} t={props.t}
              onChange={(displayName) => patchTier(tier.id, { displayName })}
            />
          </FieldBlock>

          <FieldBlock label={props.t("appWizard.subscriptions.descLabel")} hint={props.t("appWizard.subscriptions.descHint")}>
            <LocaleTextRows
              locales={locales} value={tier.description} max={APP_TEXT_LIMITS.subscriptionDescription}
              csrfToken={props.csrfToken} readOnly={props.readOnly}
              ariaLabel={props.t("appWizard.subscriptions.descLabel")} t={props.t}
              onChange={(description) => patchTier(tier.id, { description })}
            />
          </FieldBlock>
        </article>
      ))}

      {!props.readOnly && tiers.length < APP_MAX_SUBSCRIPTION_TIERS && (
        <button className="secondary-button compact-button" type="button" onClick={addTier}>
          {props.t("appWizard.subscriptions.addTier")}
        </button>
      )}
    </div>
  );
}

const EMPTY_LEGAL: AppLegalPayload = {
  legalEntityName: "",
  legalEntityAddress: "",
  businessPhone: "",
  supportEmail: "",
  supportUrl: "",
  marketingUrl: "",
  privacyPolicyUrl: "",
  termsOfServiceUrl: ""
};

const LEGAL_FIELDS: Array<{ key: keyof AppLegalPayload; max?: number; type: "text" | "email" | "url" }> = [
  { key: "legalEntityName", max: APP_TEXT_LIMITS.legalEntityName, type: "text" },
  { key: "legalEntityAddress", max: APP_TEXT_LIMITS.legalEntityAddress, type: "text" },
  { key: "businessPhone", max: APP_TEXT_LIMITS.businessPhone, type: "text" },
  { key: "supportEmail", type: "email" },
  { key: "supportUrl", type: "url" },
  { key: "marketingUrl", type: "url" },
  { key: "privacyPolicyUrl", type: "url" },
  { key: "termsOfServiceUrl", type: "url" }
];

export function StepLegal(props: StepProps) {
  const [draft, setDraft] = useState<AppLegalPayload>(props.data.wizard.steps.legal ?? EMPTY_LEGAL);
  const saver = useStepSaver("legal", props.save, props.readOnly);

  const update = (key: keyof AppLegalPayload, value: string) => {
    const next = { ...draft, [key]: value };
    setDraft(next);
    saver.queue(next as unknown as Record<string, unknown>);
  };

  return (
    <div className="appwiz-step">
      <header className="appwiz-step-heading">
        <h2>{props.t("appWizard.legal.title")}</h2>
        <p>{props.t("appWizard.legal.copy")}</p>
        <SaveBadge state={saver.state} t={props.t} />
      </header>
      {LEGAL_FIELDS.map((field) => (
        <FieldBlock key={field.key} label={props.t(`appWizard.legal.${field.key}Label`)} hint={props.t(`appWizard.legal.${field.key}Hint`)}>
          {field.max ? (
            <span className="appwiz-locale-input">
              <input
                type="text"
                value={draft[field.key]}
                maxLength={field.max}
                readOnly={props.readOnly}
                aria-label={props.t(`appWizard.legal.${field.key}Label`)}
                onChange={(event) => update(field.key, event.target.value)}
              />
              <CharCount value={draft[field.key]} max={field.max} />
            </span>
          ) : (
            <input
              className="appwiz-plain-input"
              type={field.type}
              value={draft[field.key]}
              readOnly={props.readOnly}
              placeholder={field.type === "url" ? "https://" : undefined}
              aria-label={props.t(`appWizard.legal.${field.key}Label`)}
              onChange={(event) => update(field.key, event.target.value)}
            />
          )}
        </FieldBlock>
      ))}
    </div>
  );
}
